import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import type { AvatarOption } from "@wr/contracts";
import { t } from "@wr/i18n";
import {
  Avatar,
  AvatarPicker,
  Button,
  BrandLockup,
  FormBanner,
  IconButton,
  LockedValue,
  RerollIcon,
  SuggestionChips,
  TextField,
} from "@wr/ui";
import {
  checkNickname,
  confirmIdentity,
  fetchAvatars,
  rerollNickname,
  setAvatar,
  setNickname,
} from "../lib/players";
import { useLogout } from "../app/useLogout";
import { useOverlay } from "../app/OverlayProvider";
import { useSeat } from "../app/SeatProvider";
import { useSession } from "../app/SessionProvider";
import { sanitiseNext, withNext } from "../app/routing";

/**
 * Nickname + avatar — design spec §9. Mandatory for new accounts and new
 * guests (PRD §4.4).
 *
 * **LOCKED — the app chrome does not exist on this screen.** No top bar, no
 * hamburger, no coin chip, no close, no back. The only exits are Continue and
 * Log out. Identity is a gate, not a page, and a gate with a back button is a
 * suggestion. (Structurally enforced: this route is not a child of `AppShell`.)
 *
 * **LOCKED — profanity and collision are visually indistinguishable.** Same
 * icon, same message, same suggestion chips. The server already refuses to
 * tell them apart; this screen must not reconstruct the difference from timing
 * or from the number of suggestions either.
 */

const CHECK_DEBOUNCE_MS = 400;
const DRAFT_KEY = "wr.welcome.draft";
/** The eyebrow that names the avatar radiogroup (S2 §22.13.1 rule 1). */
const AVATAR_EYEBROW_ID = "welcome-avatar-eyebrow";

type NickState = "pristine" | "checking" | "available" | "taken" | "invalid";

interface Draft {
  nickname: string;
  avatarId: string | null;
}

export function WelcomeScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { profile, reload, setProfile } = useSession();
  const { openConversion, showToast } = useOverlay();
  const { seatHeld } = useSeat();
  const logout = useLogout();

  /**
   * The deep link that started the chain, if there was one (§5, LOCKED —
   * "`?next=` survives the whole chain"). This gate is the last hop before it
   * is spent, which is exactly why T-172's B1 could hide here: the screen used
   * to navigate to a bare `/` and the parameter died one step from home.
   */
  const next = sanitiseNext(params.get("next"));

  /**
   * **Initialised from the profile this screen already has, not from `""`**
   * (phase plan S3-6 item 3; found in T-173 judgment call 5).
   *
   * The effect below fills this field, and an effect runs *after* the first
   * paint. So a registered player who tapped Continue on the very first frame
   * submitted an empty nickname and got a 422 error banner on a screen they
   * had done nothing wrong on. Sub-frame and rare — and still a real dead state
   * with a fix smaller than this comment.
   *
   * The lazy initialiser reads exactly what the effect reads, in the same
   * order, so there is one rule about what goes in this field and not two. The
   * effect stays because `profile` can arrive after mount (a cold load lands
   * here with a session and no profile yet).
   */
  const [nickname, setNicknameValue] = useState(
    () => readDraft()?.nickname || profile?.nickname || "",
  );
  const [nickState, setNickState] = useState<NickState>("pristine");
  const [helper, setHelper] = useState<string>(t("welcome.rules"));
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [avatars, setAvatars] = useState<AvatarOption[]>([]);
  const [avatarsLoading, setAvatarsLoading] = useState(true);
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarOption | null>(null);
  const [rerolling, setRerolling] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canChange = profile?.canChangeIdentity === true;

  /**
   * `resume` (§9): the player left mid-flow. The last local draft wins over
   * the server default, because the server default is the name they were
   * already trying to change.
   */
  useEffect(() => {
    if (!profile) return;
    const stored = readDraft();
    setNicknameValue(stored?.nickname || profile.nickname);
  }, [profile]);

  useEffect(() => {
    void (async () => {
      try {
        const { avatars: pool } = await fetchAvatars();
        setAvatars(pool);
        const stored = readDraft();
        const chosen =
          pool.find((option) => option.id === stored?.avatarId) ??
          pool.find((option) => option.url === profile?.avatarUrl) ??
          null;
        setSelectedAvatar(chosen);
      } catch {
        // A missing pool is a cosmetic gap, not a blocked gate: the player
        // already has an auto-assigned avatar and Continue still works.
        setAvatars([]);
      } finally {
        setAvatarsLoading(false);
      }
    })();
  }, [profile?.avatarUrl]);

  // Debounced availability check. Guests never run it — they cannot change the
  // value, so asking the server about it is a request with no possible use.
  useEffect(() => {
    if (!canChange || !profile) return;
    const candidate = nickname.trim();

    if (candidate === profile.nickname) {
      setNickState("pristine");
      setHelper(t("welcome.rules"));
      setSuggestions([]);
      return;
    }

    const rule = localRule(candidate);
    if (rule) {
      setNickState("invalid");
      setHelper(t(rule));
      setSuggestions([]);
      return;
    }

    setNickState("checking");
    setHelper(t("welcome.checking"));
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const result = await checkNickname(candidate);
          if (result.available) {
            setNickState("available");
            setHelper(t("welcome.available"));
            setSuggestions([]);
            return;
          }
          if (result.reason === "invalid") {
            setNickState("invalid");
            setHelper(t(result.rule === "length" ? "welcome.ruleLength" : "welcome.ruleCharset"));
            setSuggestions([]);
            return;
          }
          // `taken` and `unavailable` (profanity) converge here — one state,
          // one string, one set of chips. LOCKED.
          setNickState("taken");
          setHelper(t("welcome.taken"));
          setSuggestions(result.suggestions);
        } catch {
          setNickState("pristine");
          setHelper(t("welcome.rules"));
        }
      })();
    }, CHECK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [nickname, canChange, profile]);

  // Persist the draft so a mid-flow exit is resumable (§9 `resume`).
  useEffect(() => {
    if (!canChange) return;
    writeDraft({ nickname, avatarId: selectedAvatar?.id ?? null });
  }, [nickname, selectedAvatar, canChange]);

  const reroll = useCallback(async () => {
    setRerolling(true);
    try {
      const next = await rerollNickname();
      setNicknameValue(next);
    } catch {
      // §9 `reroll-failed`: the field KEEPS its current value (PRD §4.4).
      showToast(t("welcome.rerollFailed"));
    } finally {
      setRerolling(false);
    }
  }, [showToast]);

  async function submit() {
    if (!profile) return;
    setSubmitting(true);
    setError(null);

    try {
      let latest = profile;

      if (canChange) {
        const candidate = nickname.trim();
        if (candidate !== profile.nickname) latest = await setNickname(candidate);
        if (selectedAvatar && selectedAvatar.url !== latest.avatarUrl) {
          latest = await setAvatar(selectedAvatar.id);
        }
      }

      // The gate is server state: a guest goes through this too, which is why
      // it is not behind `canChange`.
      latest = await confirmIdentity();
      setProfile(latest);
      clearDraft();
      await reload();
      // Back through the splash rather than straight to /lobby: the routing
      // matrix is the one place that decides where a session belongs, and a
      // screen that hard-codes its own successor is a screen that will one day
      // send an unverified account to the lobby. The splash needs the deep
      // link to make that decision, so it is handed back on the way in.
      void navigate(withNext("/", next), { replace: true });
    } catch (caught) {
      // §9 `error`: EVERY selection is preserved. Never reset the avatar,
      // never regenerate the nickname on failure.
      setError(caught instanceof Error ? caught.message : t("welcome.saveFailed"));
    } finally {
      setSubmitting(false);
    }
  }

  if (!profile) return null;

  const previewUrl = selectedAvatar?.url ?? profile.avatarUrl;

  return (
    <main
      className="wr-screen wr-stack"
      style={{ gap: 24, paddingTop: 32, paddingBottom: 48, background: "var(--wr-surface)" }}
      data-testid="welcome"
    >
      <div className="wr-form-column wr-stack" style={{ gap: 24 }}>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <BrandLockup
            size="sm"
            wordy={t("app.wordy")}
            royale={t("app.royale")}
            appName={t("app.name")}
          />
        </div>

        <div className="wr-stack" style={{ gap: 8 }}>
          <h1 className="wr-display-lg" data-route-heading>
            {t("welcome.title")}
          </h1>
          {/* QA T-172 finding #4: the registered copy tells you to pick a
              nickname and an avatar. A guest can pick neither — the field is a
              LockedValue and the gallery is replaced (§9, guest variant,
              LOCKED) — so the first thing they read must not be an instruction
              the screen refuses. */}
          <p className="wr-body-md wr-ink-70" data-testid="welcome-body">
            {canChange ? t("welcome.body") : t("welcome.body.guest")}
          </p>
        </div>

        {error ? (
          <FormBanner tone="error" testId="welcome-error">
            {error}
          </FormBanner>
        ) : null}

        <div style={{ display: "flex", justifyContent: "center" }}>
          <Avatar
            playerId={profile.id}
            nickname={profile.nickname}
            url={previewUrl}
            size={96}
            hero
          />
        </div>

        {canChange ? (
          <TextField
            id="welcome-nickname"
            eyebrow={t("welcome.nickname")}
            value={nickname}
            readOnly={submitting}
            data-testid="welcome-nickname"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={20}
            invalid={nickState === "taken" || nickState === "invalid"}
            helper={helper}
            eyebrowAction={
              <IconButton
                large
                round
                label={t("welcome.reroll")}
                disabled={rerolling || submitting}
                data-testid="welcome-reroll"
                onClick={() => void reroll()}
              >
                <RerollIcon />
              </IconButton>
            }
            adornment={<NickAdornment state={nickState} />}
            onChange={(event) => setNicknameValue(event.target.value)}
          />
        ) : (
          /* Guest variant — LOCKED (§9): a LockedValue plate, not a disabled
             input, and the re-roll button is removed rather than greyed. */
          <LockedValue
            eyebrow={t("welcome.nickname")}
            value={profile.nickname}
            lockLabel={t("welcome.locked")}
          />
        )}

        {nickState === "taken" && suggestions.length > 0 ? (
          <SuggestionChips
            label={t("welcome.suggestions")}
            suggestions={suggestions}
            onPick={(value) => setNicknameValue(value)}
          />
        ) : null}

        {canChange ? (
          <div className="wr-stack" style={{ gap: 8 }}>
            {/*
              §22.13.1 rule 1 — the radiogroup is named by **this** node, the
              eyebrow the player can see, through `aria-labelledby`. An
              `aria-label` duplicating visible text is a second copy of one
              string that can drift from it.
            */}
            <span className="wr-mono-xs wr-ink-70" id={AVATAR_EYEBROW_ID}>
              {t("welcome.chooseLook")}
            </span>
            <AvatarPicker
              labelledBy={AVATAR_EYEBROW_ID}
              options={avatars}
              loading={avatarsLoading}
              disabled={submitting}
              selectedId={selectedAvatar?.id ?? null}
              onSelect={(option) => setSelectedAvatar(option)}
            />
          </div>
        ) : (
          /* The gallery is REPLACED, not disabled — and the replacement is an
             invitation, never a toll gate: Continue below stays fully enabled
             and the guest can proceed in one tap. */
          <div className="wr-convert-card" data-testid="welcome-guest-convert">
            <h2 className="wr-display-sm">{t("welcome.guest.convertTitle")}</h2>
            <p className="wr-body-sm wr-ink-70">{t("welcome.guest.convertBody")}</p>
            {/* §14.1 r5, LOCKED — card without the button while a seat is held. */}
            {seatHeld ? (
              <p className="wr-mono-sm wr-ink-70" data-testid="welcome-convert-blocked">
                {t("convert.blocked.in_game")}
              </p>
            ) : (
              <Button
                variant="primary"
                size="sm"
                data-testid="welcome-convert"
                onClick={openConversion}
              >
                {t("welcome.guest.convertCta")}
              </Button>
            )}
          </div>
        )}

        <Button
          variant="primary"
          size="lg"
          fullWidth
          loading={submitting}
          loadingLabel={t("common.working")}
          disabled={canChange && (nickState === "taken" || nickState === "invalid")}
          data-testid="welcome-continue"
          onClick={() => void submit()}
        >
          {t("welcome.continue")}
        </Button>

        <Button
          variant="ghost"
          size="md"
          fullWidth
          data-testid="welcome-logout"
          // §14.1 ruling C, LOCKED: the deep link is dropped, not carried. It
          // recorded an interruption the system imposed; this is the player
          // choosing to leave, and handing them back to `/settings` after their
          // next log-in would be the app remembering something they did not ask
          // it to. The guard on this route agrees, because `useLogout` raises
          // the `logging-out` transition before the session goes.
          onClick={() => void logout()}
        >
          {t("welcome.logout")}
        </Button>
      </div>
    </main>
  );
}

/** The field's status adornment. Icon + shape, never colour alone (§15.4). */
function NickAdornment({ state }: { state: NickState }) {
  if (state === "checking") {
    return <span className="wr-status-dot wr-status-dot--pending" aria-hidden="true" />;
  }
  if (state === "available") {
    return (
      <span className="wr-status-dot wr-status-dot--good" aria-hidden="true">
        ✓
      </span>
    );
  }
  if (state === "taken" || state === "invalid") {
    return (
      <span className="wr-status-dot wr-status-dot--warn" aria-hidden="true">
        ✕
      </span>
    );
  }
  return null;
}

/**
 * The rules the client can check itself — the same ones the contract encodes,
 * so the `invalid` state is a local check of the server's rule and not a guess
 * at it.
 */
function localRule(candidate: string): "welcome.ruleLength" | "welcome.ruleCharset" | null {
  if (candidate.length < 3 || candidate.length > 20) return "welcome.ruleLength";
  if (!/^[A-Za-z0-9_]+$/.test(candidate)) return "welcome.ruleCharset";
  return null;
}

function readDraft(): Draft | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}

function writeDraft(draft: Draft): void {
  try {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Private mode, quota, a locked-down browser. A lost draft is a lost
    // convenience; it must never be a lost gate.
  }
}

function clearDraft(): void {
  try {
    sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    // As above.
  }
}
